import { api } from "@service/api";
import { Card } from "@components/cards/card";
import { InputClickToCopy } from "@components/forms/input-click-to-copy";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useEffect, useState } from "react";
import {
  FaBan,
  FaCrown,
  FaEnvelopeOpenText,
  FaLock,
  FaRegClock,
  FaShareFromSquare,
  FaTrash,
  FaUserCheck,
  FaUserSlash,
} from "react-icons/fa6";
import Skeleton from "react-loading-skeleton";
import { Button } from "@components/buttons/button";
import { FaCheckCircle, FaUserTimes } from "react-icons/fa";
import { ProgressBar } from "react-bootstrap";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { ModalRaffleAffiliateRequest } from "@components/modals/modal-raffle-affiliate-request";
import {
  RaffleAffiliateStatusEnum,
  RaffleAffiliateStatusTypes,
} from "@hooks/enums/raffle_affiliate";
import { IconType } from "react-icons";
import Swal from "sweetalert2";
import { RaffleStatusEnum } from "@hooks/enums/raffle";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { RaffleStatusTypes } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { config } from "config";
import { set } from "react-hook-form";

interface IProps {
  raffleKey: string;
  campaign?: string;
  raffle: IRaffleDefaultDTO;
}

interface ILoading {
  raffle: boolean;
  ticketStatus: boolean;
  ticketProgressBar: boolean;
  campaignStatus: boolean;
  partners: boolean;
}

interface IAffiliateInfo {
  total: number;
  isAffiliate: boolean;
  commissionPercent: number;
  requestStatus: RaffleAffiliateStatusTypes | undefined;
}

interface IModalDTO {
  affiliateRequest: boolean;
}

const masks = new Masks();
export const RaffleSummaryVisitor = ({
  raffleKey,
  campaign,
  raffle,
}: IProps) => {
  const { customer } = ZuAuthStore();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [isLoading, setIsLoading] = useState<ILoading>({
    raffle: true,
    ticketProgressBar: true,
    campaignStatus: true,
    ticketStatus: true,
    partners: true,
  });
  const [isModalOpen, setIsModalOpen] = useState<IModalDTO>({
    affiliateRequest: false,
  });

  // --- Raffle Default Infos --- //
  const [shareLink, setShareLink] = useState<string>(``);
  const [soldProgress, setSoldProgress] = useState<number>(0);
  const [ticketAmount, setTicketAmount] = useState<number>(0);
  const [campaignOwnerId, setCampaignOwnerId] = useState<number>(0);
  const [soldTicketAmount, setSoldTicketAmount] = useState<number>(0);
  const [availableTicketAmount, setAvailableTicketAmount] = useState<number>(0);
  const [affiliateInfo, setAffiliateInfo] = useState<IAffiliateInfo>();
  
  const itsMyCampaign = customer && customer.customer.id === campaignOwnerId;
  const [showAffiliateButton, setShowAffiliateButton] =
    useState<boolean>(false);

  const loadRaffleTicketSummary = async () => {
    try {
      const params = `?campaign=${campaign}&key=${raffleKey}`;
      const response = await api.get(
        `/customer/raffle/default/tickets/summary${params}`,
      );
      // console.log({response});
      const _soldTicketAmount: number = response.data.sold || 0;
      setSoldTicketAmount(_soldTicketAmount);

      setCampaignOwnerId(response.data.customer);

      const totalTickets: number = response?.data?.total || 0;
      setTicketAmount(totalTickets);
      const progress =
        Math.floor((_soldTicketAmount / totalTickets) * 100) || 0;
      setAvailableTicketAmount(Number(totalTickets - _soldTicketAmount) || 0);
      setSoldProgress(progress);

      setIsLoading((prev) => ({
        ...prev,
        ticketProgressBar: false,
        ticketStatus: false,
      }));
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar informações dos Tickets vendidos.",
        type: "error",
        theme: "dark",
      });
    }
  };

  const loadRaffleAffiliateSummary = async () => {
    try {
      const params = `?campaign=${campaign}&key=${raffleKey}`;
      const response = await api.get(`/raffle/affiliate/summary${params}`);
      // console.log({ d: "affiliate", response });
      setAffiliateInfo(response.data);

      setIsLoading((prev) => ({
        ...prev,
        partners: false,
      }));
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar informações dos Tickets vendidos.",
        type: "error",
        theme: "dark",
      });
    }
  };

  const handleRespondRequest = async (status: RaffleAffiliateStatusTypes) => {
    const action =
      status === RaffleAffiliateStatusEnum.ACTIVE
        ? "ACEITAR"
        : status === RaffleAffiliateStatusEnum.CUSTOMER_BLOCK
          ? "BLOQUEAR"
          : "RECUSAR";
    const text = `Tem certeza que deseja ${action} esta requisição de Parceria?${status === RaffleAffiliateStatusEnum.CUSTOMER_BLOCK ? ` Não receberá mais proposta para esta Campanhar.` : ``}`;
    Swal.fire({
      title: "Responder a Requisição?",
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, salvar!",
      cancelButtonText: "Cancelar",
    }).then(async (result: { isConfirmed: boolean }) => {
      if (result.isConfirmed) {
        try {
          const data = {
            key: raffleKey,
            status,
          };
          await api.put(`/raffle/affiliate`, data);

          setIsLoading((prev) => ({
            ...prev,
            partners: true,
          }));

          loadRaffleAffiliateSummary();
        } catch (error: any) {
          pushToastify({
            message:
              error?.response?.data?.message ||
              "Falha ao carregar informações dos Tickets vendidos.",
            type: "error",
            theme: "dark",
          });
        }
      }
    });
  };

  const handleModal = ({
    opening,
    type,
  }: {
    opening: boolean;
    type: "affiliateRequest";
  }) => {
    setIsModalOpen((prev) => ({
      ...prev,
      affiliateRequest:
        type === "affiliateRequest" ? opening : isModalOpen.affiliateRequest,
    }));
  };

  useEffect(() => {
    if (affiliateInfo) {
      const availableRaffleStatus = [
        RaffleStatusEnum.ACTIVE,
        RaffleStatusEnum.CREATING,
        RaffleStatusEnum.PENDING,
        RaffleStatusEnum.QUEUE,
      ] as RaffleStatusTypes[];

      const affStatus = [
        RaffleAffiliateStatusEnum.INACTIVE,
        RaffleAffiliateStatusEnum.CUSTOMER_REJECTED,
        RaffleAffiliateStatusEnum.OWNER_REJECTED,
      ] as RaffleAffiliateStatusTypes[];

      const _showAffiliateButton =
        (availableRaffleStatus.includes(raffle.status as RaffleStatusEnum) &&
          !affiliateInfo.requestStatus) ||
        affStatus.includes(
          affiliateInfo.requestStatus as RaffleAffiliateStatusEnum,
        );

      setShowAffiliateButton(_showAffiliateButton);

      if (affiliateInfo.isAffiliate) {
        const shareUrl = `${shareLink}${affiliateInfo?.isAffiliate ? `&aff=${customer?.customer?.atSign}` : ``}`;
        setShareLink(shareUrl);
      }
    }
  }, [affiliateInfo]);

  useEffect(() => {
    loadRaffleTicketSummary();
    loadRaffleAffiliateSummary();

    const shareUrl = `${config.urls.store}/campaign/default?campaign=${campaign}${
      raffleKey ? `&key=${raffleKey}` : ``
    }`;
    setShareLink(shareUrl);
  }, []);

  return (
    <>
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">Resumo da Campanha</div>
          </div>
        </div>
        <div className="card-body">
          {/* Tickets & Partners Summary */}
          <div className="row mt-4">
            <div className="d-flex justify-content-between">
              {/* Tickets */}
              <div className="col-md-5 mx-auto border rounded-4">
                <div className="card-header text-center">
                  <div className="fs-5">
                    <div className="">Resumo de Tickets</div>
                  </div>
                  <div className="text-muted fs-6 p-0 m-0">
                    <small>Tickets da Campanha</small>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row d-flex justify-content-between text-center">
                    {isLoading.ticketStatus ? (
                      <>
                        <span>Carregando informações dos tickets...</span>
                        <Skeleton count={1} />
                      </>
                    ) : (
                      <>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(ticketAmount)}
                          </span>
                          <br />
                          <span>Total</span>
                        </div>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(soldTicketAmount)}
                          </span>
                          <br />
                          <span>Vendido{soldTicketAmount > 1 ? `s` : ``}</span>
                        </div>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(availableTicketAmount)}
                          </span>
                          <br />
                          <span>
                            Disponíve
                            {availableTicketAmount > 1 ? `is` : `l`}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Partners */}
              <div className="col-md-5 mx-auto border rounded-4">
                <div className="card-header text-center">
                  <div className="fs-5">
                    <div className="">Participações</div>
                  </div>
                  <div className="text-muted fs-6 p-0 m-0">
                    <small>Parceiros | Afiliações</small>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row d-flex justify-content-between text-center">
                    {isLoading.partners || !affiliateInfo ? (
                      <>
                        <span>Carregando informações de parceiros...</span>
                        <Skeleton count={1} />
                      </>
                    ) : (
                      <>
                        <div
                          className={`col-md-12 ${affiliateInfo.isAffiliate || affiliateInfo?.commissionPercent ? `col-xxl-4` : `col-xxl-6`}`}
                        >
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(affiliateInfo?.total || 0)}
                          </span>
                          <br />
                          <span>Afiliados</span>
                        </div>
                        <div
                          className={`col-md-12 ${affiliateInfo.isAffiliate || affiliateInfo?.commissionPercent ? `col-xxl-4` : `col-xxl-6`}`}
                        >
                          <div className="d-flex flex-column justify-content-center align-items-center gap-1">
                            {itsMyCampaign ? (
                              <>
                                <FaCrown
                                  className="fs-2"
                                  style={{ color: "#FFD700" }}
                                  title="Você é o proprietário(a) desta Campanha."
                                />

                                <span>Proprietário</span>
                              </>
                            ) : (
                              <>
                                {affiliateInfo && affiliateInfo.isAffiliate ? (
                                  <>
                                    <FaCheckCircle
                                      className="text-success fs-2"
                                      title={`Você já está afiliado(a) a esta Campanha.`}
                                    />
                                    <span>Afiliado</span>
                                  </>
                                ) : (
                                  <>
                                    {(() => {
                                      const statusConfig: Record<
                                        RaffleAffiliateStatusEnum,
                                        {
                                          Icon: IconType;
                                          className: string;
                                          label: string;
                                          title: string;
                                        }
                                      > = {
                                        [RaffleAffiliateStatusEnum.CUSTOMER_REQUEST]:
                                          {
                                            Icon: FaRegClock,
                                            className: "text-info fs-2",
                                            label: "Aguardando Aprovação",
                                            title:
                                              "Sua solicitação está aguardando aprovação do organizador",
                                          },
                                        [RaffleAffiliateStatusEnum.OWNER_REQUEST]:
                                          {
                                            Icon: FaEnvelopeOpenText,
                                            className: "text-primary fs-2",
                                            label: "Convite Recebido",
                                            title:
                                              "Você recebeu um convite para ser afiliado desta campanha",
                                          },
                                        [RaffleAffiliateStatusEnum.CUSTOMER_REJECTED]:
                                          {
                                            Icon: FaUserTimes,
                                            className: "text-danger fs-2",
                                            label: "Rejeitado por Você",
                                            title:
                                              "Você recusou a solicitação de afiliação",
                                          },
                                        [RaffleAffiliateStatusEnum.OWNER_REJECTED]:
                                          {
                                            Icon: FaBan,
                                            className: "text-danger fs-2",
                                            label: "Rejeitado",
                                            title:
                                              "Sua solicitação foi recusada pelo organizador",
                                          },
                                        [RaffleAffiliateStatusEnum.OWNER_BLOCK]:
                                          {
                                            Icon: FaLock,
                                            className: "text-danger fs-2",
                                            label: "Bloqueado",
                                            title:
                                              "Você não pode enviar requisição de parceria nesta Campanha",
                                          },
                                        [RaffleAffiliateStatusEnum.CUSTOMER_BLOCK]:
                                          {
                                            Icon: FaLock,
                                            className: "text-danger fs-2",
                                            label: "Bloqueada",
                                            title:
                                              "Você bloqueou e não receberá mais requisições desta Campanha",
                                          },
                                        [RaffleAffiliateStatusEnum.DELETED]: {
                                          Icon: FaTrash,
                                          className: "text-muted fs-2",
                                          label: "Removido",
                                          title: "Sua afiliação foi removida",
                                        },
                                        [RaffleAffiliateStatusEnum.ACTIVE]: {
                                          Icon: FaUserCheck,
                                          className: "text-success fs-2",
                                          label: "Ativo",
                                          title:
                                            "Você está afiliado a esta campanha",
                                        },
                                        [RaffleAffiliateStatusEnum.INACTIVE]: {
                                          Icon: FaUserSlash,
                                          className: "text-secondary fs-2",
                                          label: "Inativo",
                                          title: "Sua afiliação está inativa",
                                        },
                                      };

                                      const config =
                                        affiliateInfo?.requestStatus
                                          ? statusConfig[
                                              affiliateInfo.requestStatus
                                            ]
                                          : null;

                                      if (!config) {
                                        return (
                                          <>
                                            <FaUserSlash
                                              className="text-warning fs-2"
                                              title="Você ainda não está afiliado(a) a esta Campanha"
                                            />
                                            <span>Não Afiliado</span>
                                          </>
                                        );
                                      }

                                      const { Icon, className, label, title } =
                                        config;
                                      return (
                                        <>
                                          <Icon
                                            className={className}
                                            title={title}
                                          />
                                          <span>{label}</span>
                                        </>
                                      );
                                    })()}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {(affiliateInfo.isAffiliate ||
                          affiliateInfo?.commissionPercent > 0) && (
                          <div className="col-md-12 col-xxl-4">
                            <span className="fs-4 fw-bold">
                              {affiliateInfo?.commissionPercent || 0}%
                            </span>
                            <br />
                            <span>Sua Comissão</span>
                          </div>
                        )}

                        {/* Button */}
                        {!itsMyCampaign && (
                          <>
                            <div className="row col-6 mx-auto mt-4">
                              {showAffiliateButton ? (
                                <Button
                                  className={`btn btn-primary rounded text-white`}
                                  onClick={() => {
                                    handleModal({
                                      opening: true,
                                      type: "affiliateRequest",
                                    });
                                  }}
                                >
                                  Solicitar Afiliação
                                </Button>
                              ) : (
                                <>
                                  {affiliateInfo.requestStatus ===
                                    RaffleAffiliateStatusEnum.OWNER_REQUEST && (
                                    <div className="d-flex justify-content-center gap-3">
                                      <Button
                                        className={`btn btn-success rounded text-white w-100`}
                                        onClick={() => {
                                          handleRespondRequest(
                                            RaffleAffiliateStatusEnum.ACTIVE,
                                          );
                                        }}
                                      >
                                        Aceitar
                                      </Button>
                                      <Button
                                        className={`btn btn-warning rounded text-white w-100`}
                                        onClick={() => {
                                          handleRespondRequest(
                                            RaffleAffiliateStatusEnum.CUSTOMER_REJECTED,
                                          );
                                        }}
                                      >
                                        Recusar
                                      </Button>
                                      <Button
                                        className={`btn btn-danger rounded text-white w-100`}
                                        onClick={() => {
                                          handleRespondRequest(
                                            RaffleAffiliateStatusEnum.CUSTOMER_BLOCK,
                                          );
                                        }}
                                      >
                                        Bloquear
                                      </Button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="row my-4">
            <div className="col-md-11 mx-auto text-center">
              {isLoading.ticketProgressBar ? (
                <>
                  <span>Carregando progresso da Campanha...</span>
                  <Skeleton count={1} />
                </>
              ) : (
                <>
                  <div className="d-flex flex-column gap-2">
                    <span className="fs-6 fw-bold">
                      {soldProgress}% dos tickets já foram vendidos.
                    </span>
                    <ProgressBar
                      now={soldProgress}
                      label={`${soldProgress}%`}
                      variant="primary"
                      animated
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <hr />

          {/* Share this Campaign */}
          <div className="row">
            <div className="col-10 mx-auto">
              <div className="text-center mb-3">
                <FaShareFromSquare className="fs-5 me-1" />
                <InputClickToCopy
                  label={
                    affiliateInfo && affiliateInfo.isAffiliate
                      ? `Seu link de Afiliado`
                      : `Link da Campanha`
                  }
                  value={shareLink}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ModalRaffleAffiliateRequest
        raffleKey={raffleKey}
        customerId={customer?.customer.id || 0}
        isOpen={isModalOpen.affiliateRequest}
        onClose={(updated) => {
          handleModal({ opening: false, type: "affiliateRequest" });
          if (updated) {
            loadRaffleAffiliateSummary();
          }
        }}
      />
    </>
  );
};
